import React from "react";
import { clp } from "@/lib/api";
import { maskRut } from "@/lib/rut";
import { saleReceiptLines } from "@/lib/saleLines";

/** Cuerpo del ticket de venta. Se usa en el modal y en la impresión inmediata sin vista previa. */
export const SaleTicket = ({ sale, companyName, storeName, graciasMessage }) => {
  if (!sale) return null;
  const lineas = saleReceiptLines(sale);
  const gracias = String(graciasMessage ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <div className="bg-white p-4 rounded-lg font-mono-num text-sm" id="ticket-print">
      <div className="text-center mb-3">
        <p className="font-bold">{companyName}</p>
        <p className="text-xs text-slate-500">{storeName}</p>
        {sale.offline && (
          <p data-testid="pos-ticket-offline-badge" className="mt-1 inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold uppercase tracking-widest">
            Comprobante OFFLINE · pendiente de sincronizar
          </p>
        )}
        {sale.payment_is_simulated && (
          <p data-testid="pos-ticket-simulated-badge" className="mt-1 inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300 text-[10px] font-bold uppercase tracking-widest">
            Pago simulado · modo de pruebas · no es un cobro real
          </p>
        )}
        <p className="text-xs text-slate-500 mt-1">Venta #{sale.numero}</p>
        <p className="text-xs text-slate-500">{new Date(sale.created_at).toLocaleString("es-CL")}</p>
      </div>
      {sale.numero_atencion ? (
        <div data-testid="ticket-numero-atencion" className="border-t border-dashed py-2 text-center">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">N° de atención</p>
          <p className="text-4xl font-black font-mono-num leading-none">{sale.numero_atencion}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Te llamaremos por este número</p>
        </div>
      ) : null}
      <div className="border-t border-dashed py-2 space-y-1">
        {lineas.items.map((it, i) => (
          <div key={i} className="flex justify-between text-xs">
            <span>{it.cantidad}x [{it.sku}] {it.nombre}</span><span>{clp(it.amount_normal)}</span>
          </div>
        ))}
      </div>
      {(sale.customer_rut || sale.customer_name) && (
        <div data-testid="pos-ticket-customer" className="border-t border-dashed pt-2 text-xs">
          {sale.customer_name && <p>Cliente: {sale.customer_name}</p>}
          {sale.customer_rut && (
            <>
              {/* En pantalla: RUT completo · Impreso: enmascarado por seguridad del cliente */}
              <p className="print:hidden" data-testid="pos-ticket-rut-screen">RUT: {sale.customer_rut}</p>
              <p className="hidden print:block" data-testid="pos-ticket-rut-print">RUT: {maskRut(sale.customer_rut)}</p>
            </>
          )}
        </div>
      )}
      <div className="border-t border-dashed pt-2 space-y-1 text-xs">
        {/* Productos a precio normal arriba · los descuentos se detallan aquí */}
        {lineas.discounts.length > 0 ? (
          <>
            <div className="flex justify-between"><span>Subtotal</span><span>{clp(lineas.subtotal_normal)}</span></div>
            {lineas.discounts.map((d, i) => (
              <div key={i} data-testid={`pos-ticket-descuento-${d.kind.toLowerCase()}`}
                   className="flex justify-between text-emerald-700"><span>{d.label}</span><span>-{clp(d.value)}</span></div>
            ))}
          </>
        ) : null}
        {lineas.extras.map((ex, i) => (
          <div key={i} className="flex justify-between"><span>{ex.label}</span><span>{clp(ex.value)}</span></div>
        ))}
        {Math.round(sale.total_exento || 0) > 0 && (
          <div className="flex justify-between"><span>Exento</span><span>{clp(Math.round(sale.total_exento))}</span></div>
        )}
        {Math.round(sale.total_afecto ?? (sale.total - (sale.total_exento || 0))) > 0 && (
          <>
            <div className="flex justify-between"><span>Neto</span><span>{clp(Math.round(sale.neto_afecto ?? (sale.total - sale.iva)))}</span></div>
            <div className="flex justify-between"><span>IVA</span><span>{clp(Math.round(sale.iva_afecto ?? sale.iva ?? 0))}</span></div>
          </>
        )}
        <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span>{clp(sale.total)}</span></div>
        {sale.redondeo_ajuste ? (
          <>
            <div data-testid="pos-ticket-rounding" className="flex justify-between"><span>Redondeo legal</span><span>{sale.redondeo_ajuste > 0 ? "+" : ""}{clp(sale.redondeo_ajuste)}</span></div>
            <div className="flex justify-between font-bold"><span>A pagar</span><span>{clp(sale.total_pagar ?? sale.total)}</span></div>
          </>
        ) : null}
        {(sale.payments || []).length > 1 ? (
          <div data-testid="pos-ticket-payments" className="pt-1 space-y-0.5">
            {sale.payments.map((p, i) => (
              <div key={i} className="flex justify-between">
                <span>{p.nombre || String(p.codigo || "").replace(/_/g, " ")}</span><span>{clp(p.monto_cobrado ?? p.monto)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-between"><span>Pago</span><span>{sale.payment_label || sale.payment_method}</span></div>
        )}
        {sale.credito && (
          <div className="flex justify-between" data-testid="ticket-tp-cupo">
            <span>{sale.credito.nombre} · cupo disponible</span><span>{clp(sale.credito.disponible)}</span>
          </div>
        )}
        {sale.vuelto ? <div className="flex justify-between"><span>Vuelto</span><span>{clp(sale.vuelto)}</span></div> : null}
      </div>
      <div className="mt-3" data-testid="sale-ticket-gracias">
        {(gracias.length ? gracias : ["¡Gracias por tu compra!"]).map((l, i) => (
          <p key={i} className="text-center text-xs text-slate-500">{l}</p>
        ))}
      </div>
      {(sale.quote_numero || sale.quote_id) && (
        <p data-testid="sale-ticket-quote" className="text-center text-xs text-slate-500">
          Según cotización {sale.quote_numero || sale.quote_id}
        </p>
      )}
      {(sale.customer_name || sale.customer_rut) && (
        <p data-testid="sale-ticket-customer" className="text-center text-xs text-slate-500">
          Cliente: {[sale.customer_name, sale.customer_rut].filter(Boolean).join(" · ")}
        </p>
      )}
    </div>
  );
};
